import React from "react";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { toVector3, projectDimensionPoint } from "./dimensionMath";

function getWorkPlaneNormal(workPlane) {
  const plane = workPlane?.plane || "xy";

  if (plane === "xy") return new THREE.Vector3(0, 0, 1);
  if (plane === "xz") return new THREE.Vector3(0, 1, 0);
  if (plane === "yz") return new THREE.Vector3(1, 0, 0);

  return new THREE.Vector3(0, 0, 1);
}

export default function DimensionLine3D({
  dimension,
  points,
  mode = "free",
  markerSize = 1.5,
  defaultOffset = 15,
  workPlane = { plane: "xy", offset: 0 },
  selected = false,
  onSelect,
}) {
  const sourcePoints = points || dimension?.points || [];
  const id = dimension?.id;

  if (!Array.isArray(sourcePoints) || sourcePoints.length < 2) return null;

  const p1 = sourcePoints[0]?.world;
  const p2 = sourcePoints[1]?.world;
  const p3 = sourcePoints[2]?.world || null;

  if (!p1 || !p2) return null;

  const a = toVector3(p1);
  const rawB = toVector3(p2);
  const b = projectDimensionPoint(a, rawB, mode);

  const dir = new THREE.Vector3().subVectors(b, a);
  const distance = dir.length();

  if (distance <= 0.0001) return null;

  const lineDir = dir.clone().normalize();
  const workNormal = getWorkPlaneNormal(workPlane);

  // Offset direction mora da bude u work plane ravni i normalan na dimenzionu liniju.
  let offsetDir = new THREE.Vector3().crossVectors(workNormal, lineDir);

  if (offsetDir.length() <= 0.001) {
    offsetDir = new THREE.Vector3().crossVectors(
      new THREE.Vector3(0, 1, 0),
      lineDir
    );
  }

  if (offsetDir.length() <= 0.001) {
    offsetDir = new THREE.Vector3().crossVectors(
      new THREE.Vector3(1, 0, 0),
      lineDir
    );
  }

  offsetDir.normalize();

 const manualOffset =
  dimension?.offsetDistance !== undefined &&
  dimension?.offsetDistance !== null &&
  dimension?.offsetDistance !== "";

let offsetDistance = manualOffset
  ? Number(dimension.offsetDistance)
  : Number(defaultOffset || 15);

if (!manualOffset && p3) {
  const placement = toVector3(p3);
  const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
  const toPlacement = new THREE.Vector3().subVectors(placement, mid);

  const projectedOffset = toPlacement.dot(offsetDir);

  if (Math.abs(projectedOffset) > 0.001) {
    offsetDistance = projectedOffset;
  }
}

  const offsetVector = offsetDir.clone().multiplyScalar(offsetDistance);

  const aDim = a.clone().add(offsetVector);
  const bDim = b.clone().add(offsetVector);
  const midDim = new THREE.Vector3().addVectors(aDim, bDim).multiplyScalar(0.5);

  // Label neka bude u centru dimenzione linije,
// samo malo podignut sa work plane normalom da ne upadne u liniju/model.
const labelLift = workNormal.clone().multiplyScalar(4);
const labelPos = midDim.clone().add(labelLift);

  const extGeomA = new THREE.BufferGeometry().setFromPoints([a, aDim]);
  const extGeomB = new THREE.BufferGeometry().setFromPoints([b, bDim]);
  const dimGeom = new THREE.BufferGeometry().setFromPoints([aDim, bDim]);

  const tickHalf = Math.max(markerSize * 1.8, 4);
  const tickDir = offsetDir.clone().multiplyScalar(tickHalf);

  const tickAGeom = new THREE.BufferGeometry().setFromPoints([
    aDim.clone().sub(tickDir),
    aDim.clone().add(tickDir),
  ]);

  const tickBGeom = new THREE.BufferGeometry().setFromPoints([
    bDim.clone().sub(tickDir),
    bDim.clone().add(tickDir),
  ]);

  const dimColor = selected ? "#2563eb" : "#f97316";
  const extColor = selected ? "#60a5fa" : "#64748b";

  const computedLabel =
  mode === "free"
    ? `${distance.toFixed(2)} mm`
    : `${mode.toUpperCase()}: ${distance.toFixed(2)} mm`;

const label = dimension?.label || computedLabel;

  return (
    <group>
      <line geometry={extGeomA} renderOrder={50}>
        <lineBasicMaterial color={extColor} depthTest={false} />
      </line>

      <line geometry={extGeomB} renderOrder={50}>
        <lineBasicMaterial color={extColor} depthTest={false} />
      </line>

      <line geometry={dimGeom} renderOrder={51}>
        <lineBasicMaterial color={dimColor} depthTest={false} />
      </line>

      <line geometry={tickAGeom} renderOrder={52}>
        <lineBasicMaterial color={dimColor} depthTest={false} />
      </line>

      <line geometry={tickBGeom} renderOrder={52}>
        <lineBasicMaterial color={dimColor} depthTest={false} />
      </line>

      <Html
  position={[labelPos.x, labelPos.y, labelPos.z]}
  center
  occlude={false}
  zIndexRange={[100000, 0]}
  style={{
    pointerEvents: "none",
    userSelect: "none",
  }}
>
  <div
    onClick={(e) => {
      e.stopPropagation();
      onSelect?.(id);
    }}
    style={{
      pointerEvents: "auto",
      padding: "8px 12px",
      borderRadius: 8,
      background: selected ? "#dbeafe" : "rgba(255,255,255,0.98)",
      border: selected ? "2px solid #2563eb" : "1px solid #94a3b8",
      fontSize: 18,
      lineHeight: "20px",
      fontWeight: 900,
      color: "#0f172a",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
      cursor: "pointer",
    }}
  >
    {label}
  </div>
</Html>
    </group>
  );
}